/**
 * Picture tier ("Vẽ tranh", docs/CURRICULUM-V2.md "Bổ sung 2026-10-19", AUTHORING.md "v3"): every core lesson and
 * remix card gets a FULL PICTURE = the object recapped in a new pose + its scenery + sky / ground colouring + light
 * and cast shadows + a signature. The picture's vignette (FLUX background + the posed object) is its card art.
 *
 *   export const cozyHousePicture = (get) => pictureDrawing({
 *     id: 'intro-cozy-house-picture', title: t('House on the Hill', 'Ngôi Nhà Trên Đồi'), world: 'intro', level: 2,
 *     of: 'intro-cozy-house', activity: 'intro-cozy-house', pose: [{ remove: ['garden'] }],
 *     place: { x: 120, y: 132, scale: 0.62 }, horizon: 150,
 *     parts: [sky(150), ground(150, 'grass', 6), cloud([[60, 40, 18]]), dirtPath(150, 120), trees([[24, 160, 60]])],
 *   }, get);
 *
 * Layer part names: backdrop `bd:<part>` (one element = one part), the object `0:<part>`, guides `horizon` + `slot:0`.
 * Rule "shown ⇒ taught": every element gets ≥ 1 draw step and ≥ 1 colour step (`pictureProblems`, validator).
 */
import { t, tf, type L } from '../../text';
import { ART_PLAN_ERRORS, artDrawing, type ArtStepDef } from '../art/compile';
import { ellipse } from '../art/geom';
import { appearStage, type ArtFigure, type ArtLayer } from '../art/types';
import type { Difficulty, Drawing, PartSwap, PathId, PictureInfo, PictureLight, PictureLightKind } from '../model';
import { composeFigure, placedBox } from './compose';
import { remixFigure } from './remix';
import { castInfo, type PicturePart } from './pictureKit';

export interface PictureSpec {
  id: string;
  title: L;
  world: PathId;
  /** World level of the object (1–4): difficulty + scaffold. */
  level: 1 | 2 | 3 | 4;
  /** The object drawing (core / remix card / playground-only). */
  of: string;
  /** Its path activity id (core id or remix node id); omit for playground-only items. */
  activity?: string;
  /** New pose of the object (part swaps; also drop the figure's own backdrop parts like `garden`, `sea`, `table`). */
  pose?: PartSwap[];
  /** Object centre / scale in the 240 picture (like a scene placement). */
  place: { x: number; y: number; scale: number; flip?: boolean };
  /** Horizon y (composition guide). */
  horizon: number;
  /** Background elements, back → front (sky / ground first). */
  parts: PicturePart[];
  /** Object cast shadow on the ground; false for things in the air (default: under the object, to the right). */
  cast?: false | [number, number, number];
  /** Object name in captions (default: the object's title). */
  objectLabel?: L;
  minutes?: number;
  /** Where the picture's ONE light comes from (default: derived from a sun / moon / window / lamp element, else the world). */
  light?: PictureLight;
}

const PICTURE_SKILLS = ['shading', 'details', 'creativity'] as const;
const FRAME = 'M0 0L240 0L240 240L0 240Z';
const SIGN = 'M196 226C199 220 203 228 206 222C208 219 211 226 214 223C216 221 219 225 224 224';

/** Every distinct part name of the composed figure with the prefix, in figure order. */
const partsWith = (fig: ArtFigure, pre: string, pick: (l: ArtLayer) => boolean = () => true) => [...new Set(fig.layers.filter((l) => (l.p ?? '').startsWith(pre) && pick(l)).map((l) => l.p!))];
const guideParts = (fig: ArtFigure, pre: string) => [...new Set(fig.guides.filter((g) => (g.p ?? '').startsWith(pre)).map((g) => g.p!))];

const nums = (d: string) => (d.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number);
const LIGHT_PARTS: [RegExp, PictureLightKind][] = [[/^sun$|^sunDisc$/i, 'sun'], [/^(?!paper).*moon(?!light|bow)/i, 'moon'], [/window/i, 'window'], [/lamp|lantern|nightLight|candle/i, 'lamp']];
const WORLD_LIGHT: Partial<Record<PathId, PictureLight>> = { anime: { kind: 'moon', x: 36, y: 30 }, cuteObjects: { kind: 'window', x: 20, y: 64 } };
const clampL = (v: number) => Math.round(Math.min(226, Math.max(14, v)));

/** The picture's one light: an explicit `spec.light`, else the first sun / moon / window / lamp element (its centre), else the world default (sun top left). */
export function pictureLight(spec: Pick<PictureSpec, 'world' | 'parts' | 'light'>): PictureLight {
  if (spec.light) return spec.light;
  for (const [re, kind] of LIGHT_PARTS)
    for (const p of spec.parts) {
      if (!re.test(p.part) || (p.kind !== 'scenery' && p.kind !== 'prop')) continue;
      const n = p.layers.flatMap((l) => nums(l.d));
      const xs = n.filter((_, i) => i % 2 === 0);
      const ys = n.filter((_, i) => i % 2 === 1);
      if (!xs.length) continue;
      return { kind, x: clampL((Math.min(...xs) + Math.max(...xs)) / 2), y: clampL((Math.min(...ys) + Math.max(...ys)) / 2) };
    }
  return WORLD_LIGHT[spec.world] ?? { kind: 'sun', x: 0, y: 0 }; // the sun just off the top-left corner
}

const LIGHT_NAME: Record<PictureLightKind, L> = { sun: t('sun', 'mặt trời'), moon: t('moon', 'mặt trăng'), window: t('window', 'cửa sổ'), lamp: t('lamp', 'ngọn đèn') };
const LIT_SIDE: Record<PictureLightKind, L> = {
  sun: t('lighter on the sunny side', 'phía có nắng sáng hơn'),
  moon: t('lighter on the moonlit side', 'phía trăng chiếu sáng hơn'),
  window: t('lighter on the side near the window', 'phía gần cửa sổ sáng hơn'),
  lamp: t('lighter on the side near the lamp', 'phía gần đèn sáng hơn'),
};
/** "top left" / "bottom right"… of a light position in the 240 picture. */
export function lightPlace(l: Pick<PictureLight, 'x' | 'y'>): L {
  const v: [string, string] = l.y < 100 ? ['top', 'trên'] : l.y > 150 ? ['bottom', 'dưới'] : ['', ''];
  const h: [string, string] = l.x < 96 ? ['left', 'bên trái'] : l.x > 144 ? ['right', 'bên phải'] : ['middle', 'ở giữa'];
  if (!v[0]) return t(`on the ${h[0]}`, h[1]);
  return h[0] === 'middle' ? t(`at the ${v[0]}`, `phía ${v[1]}`) : t(`at the ${v[0]} ${h[0]}`, `góc ${v[1]} ${h[1]}`);
}
/** Shadows fall away from the light: the side word for the shadow. */
export const shadowSide = (l: Pick<PictureLight, 'x'>): L => (l.x > 120 ? t('left', 'bên trái') : t('right', 'bên phải'));

/** The generated picture plan (AUTHORING v3): composition → recap → horizon → each element → details → erase → colour → light → sign. */
export function picturePlan(fig: ArtFigure, spec: PictureSpec, obj: L): ArtStepDef[] {
  const light = pictureLight(spec);
  const bd = (p: string) => `bd:${p}`;
  const objGuides = guideParts(fig, '0:');
  const objOutline = partsWith(fig, '0:', (l) => appearStage(l) === 2 && l.p !== '0:ground');
  const objAll = partsWith(fig, '0:', (l) => l.p !== '0:ground');
  const regions = spec.parts.filter((p) => p.kind === 'sky' || p.kind === 'ground');
  const scenery = spec.parts.filter((p) => p.kind === 'scenery');
  const props = spec.parts.filter((p) => p.kind === 'prop');
  // Indoor pictures (a wall instead of a sky) have no horizon: the wall meets the floor / table top.
  const wallEl = regions.find((p) => p.kind === 'sky' && /wall/i.test(p.label.en));
  const floorEl = regions.find((p) => p.kind === 'ground');
  const floor = floorEl?.label ?? t('floor', 'sàn nhà');
  const indoor = !!wallEl;
  const steps: ArtStepDef[] = [
    {
      stage: 1,
      parts: ['horizon', 'slot:0', ...guideParts(fig, 'bd:')],
      caption: indoor
        ? tf('Press lightly: the line where the *wall* meets the *{floor}*, then a *box* where your {obj} will go', 'Nhẹ tay: kẻ đường nơi *tường* gặp *{floor}*, rồi vẽ một *khung* cho {obj}', { floor, obj })
        : tf('Press lightly: the *horizon* line, then a *box* where your {obj} will go', 'Nhẹ tay: kẻ *đường chân trời*, rồi vẽ một *khung* cho {obj}', { obj }),
      tip: indoor
        ? t('Big picture first: where is the wall, where is the floor or table, where is your hero?', 'Nhìn tổng thể trước: tường ở đâu, sàn hay mặt bàn ở đâu, nhân vật chính đứng chỗ nào?')
        : t('Big picture first: where is the sky, where is the ground, where is your hero?', 'Nhìn tổng thể trước: trời ở đâu, đất ở đâu, nhân vật chính đứng chỗ nào?'),
    },
    objGuides.length
      ? { stage: [1, 2], partsByStage: { 1: objGuides, 2: objOutline }, caption: tf('Draw your *{obj}* again inside the box: quick, you know it!', 'Vẽ lại *{obj}* trong khung: nhanh thôi, con thuộc rồi!', { obj }) }
      : { stage: 2, parts: objOutline, caption: tf('Draw your *{obj}* again inside the box: quick, you know it!', 'Vẽ lại *{obj}* trong khung: nhanh thôi, con thuộc rồi!', { obj }) },
    {
      stage: 2,
      parts: regions.map((p) => bd(p.part)),
      caption: indoor
        ? tf('Go over the *corner line* firmly: wall above, {floor} below', 'Tô đậm *đường góc tường*: tường ở trên, {floor} ở dưới', { floor })
        : t('Go over the *horizon* firmly: sky above, ground below', 'Tô đậm *đường chân trời*: trời ở trên, đất ở dưới'),
    },
  ];
  for (const p of [...scenery, ...props]) steps.push({ stage: 2, parts: [bd(p.part)], caption: p.draw ?? tf('Draw the *{part}*', 'Vẽ *{part}*', { part: p.label }) });
  steps.push({ stage: 3, caption: tf('Add the *details* on your {obj}', 'Thêm *chi tiết* cho {obj}', { obj }) });
  steps.push({ stage: 4, caption: t('*Erase* the box and the helper lines', '*Tẩy* khung và các nét phụ') });
  for (const p of regions) steps.push({ stage: 5, parts: [bd(p.part)], caption: p.colour ?? tf('Colour the *{part}*', 'Tô màu *{part}*', { part: p.label }) });
  if (scenery.length)
    steps.push({
      stage: 5,
      parts: scenery.map((p) => bd(p.part)),
      caption: tf('Colour the *{parts}*: {litSide}, darker on the other side', 'Tô *{parts}*: {litSide}, phía kia tối hơn', { parts: scenery.map((p) => p.label), litSide: LIT_SIDE[light.kind] }),
    });
  if (props.length) steps.push({ stage: 5, parts: props.map((p) => bd(p.part)), caption: tf('Colour the *{parts}*', 'Tô màu *{parts}*', { parts: props.map((p) => p.label) }) });
  steps.push({ stage: 5, parts: objAll, caption: tf('Colour your *{obj}*: base colour first, then the shady side', 'Tô *{obj}*: màu nền trước, rồi đến phía bóng râm', { obj }) });
  steps.push({
    stage: 5,
    parts: ['bd:light', 'bd:cast', 'bd:objCast', '0:ground'],
    caption: tf(
      'Light and *shadows*: the {light} is {place}, so add a soft glow there and let the shadows fall to the {side}',
      'Ánh sáng và *bóng đổ*: {light} ở {place}, nên tô ánh sáng dịu quanh đó và cho bóng đổ về {side}',
      { light: LIGHT_NAME[light.kind], place: lightPlace(light), side: shadowSide(light) },
    ),
  });
  steps.push({ stage: 5, parts: ['bd:sign'], caption: t('*Sign* your picture in the corner: it is a real artwork now!', '*Ký tên* vào góc tranh: giờ đây là một tác phẩm thật rồi!') });
  return steps;
}

/** Build a picture drawing (Tier 2) from its spec; `get` resolves the object drawing. */
export function pictureDrawing(spec: PictureSpec, get: (id: string) => Drawing | undefined): Drawing {
  const object = get(spec.of);
  if (!object?.art) throw new Error(`picture ${spec.id}: object ${spec.of} has no art`);
  // The content pack resolves the worlds twice (cross-world references): the same picture of the same object art
  // is composed once (start-up time, docs/PERF.md).
  const memo = PICTURE_MEMO.get(spec.id);
  if (memo && memo.art === object.art && memo.key === pictureKey(spec)) return memo.d;
  const d = pictureDrawingUncached(spec, object);
  PICTURE_MEMO.set(spec.id, { art: object.art!, key: pictureKey(spec), d });
  return d;
}
const PICTURE_MEMO = new Map<string, { art: ArtFigure; key: string; d: Drawing }>();
const pictureKey = (spec: PictureSpec) => JSON.stringify(spec);

function pictureDrawingUncached(spec: PictureSpec, object: Drawing): Drawing {
  const posed = remixFigure(object.art!, spec.pose ?? []);
  const obj = spec.objectLabel ?? object.title;
  const layers: ArtLayer[] = [];
  const guides: ArtFigure['guides'] = [];
  // Light: a soft glow where the picture's one light is (sun / moon / window / lamp); every cast shadow on the
  // ground falls AWAY from it (left light → shadows to the right, and the other way round).
  const light = pictureLight(spec);
  const away = light.x > 120 ? -1 : 1;
  for (const p of spec.parts) {
    // Background inks are drawn with their element (stage 2), so each element is ONE draw step.
    // Scenery may bleed off the edge: clipped to the picture frame.
    for (const l of p.layers) {
      const c = away < 0 ? castInfo(l) : undefined;
      const d = c ? { d: ellipse(2 * c.ox - c.cx, c.cy, c.rx, c.ry) } : {};
      layers.push({ ...l, ...d, ...(l.k === 'ink' && l.stage === undefined ? { stage: 2 as const } : {}), ...(l.clip ? {} : { clip: FRAME }) });
    }
    for (const g of p.guides ?? []) guides.push(g);
  }
  for (const r of [78, 54, 32]) layers.push({ k: 'spot', p: 'light', d: ellipse(light.x, light.y, r, r * 0.9), fill: 'picGlow', opacity: 0.16, clip: FRAME });
  const placement = { drawingId: spec.of, ...spec.place, z: 1, label: obj };
  if (spec.cast !== false) {
    const b = placedBox(posed, placement);
    const auto: [number, number, number] = b ? [b.x + b.w * (0.5 + 0.08 * away), b.y + b.h - 1, b.w * 0.46] : [120 + 10 * away, 200, 40];
    // An authored cast shadow is mirrored about the object when the light sits on the right.
    const given = spec.cast && b && away < 0 && spec.cast[0] > b.x + b.w / 2 ? ([2 * (b.x + b.w / 2) - spec.cast[0], spec.cast[1], spec.cast[2]] as [number, number, number]) : spec.cast;
    const [cx, cy, rx] = given ?? auto;
    if (!posed.ground) layers.push({ k: 'spot', p: 'objCast', d: ellipse(cx, cy, rx, Math.max(3, rx * 0.14)), fill: 'shade', opacity: 0.3 });
  }
  layers.push({ k: 'ink', p: 'sign', d: SIGN, w: 0.7, color: 'picSign', taper: 'end', stage: 5 });
  const fig = composeFigure({ backdrop: { vb: [240, 240], layers, guides }, horizonY: spec.horizon, placements: [placement] }, (id) => (id === spec.of ? posed : undefined));
  const plan = picturePlan(fig, spec, obj);
  const d = artDrawing(spec.id, spec.title, { subject: object.subject, category: object.category, skills: [...PICTURE_SKILLS], difficulty: Math.min(3, Math.max(1, spec.level - 1)) as Difficulty, minutes: spec.minutes ?? 12 + spec.level * 2 }, fig, plan);
  // Drop a details step that picks nothing (object without stage-3 details).
  const errs = ART_PLAN_ERRORS.get(spec.id);
  if (errs?.length && errs.every((e) => /picks nothing \(parts \* at stage 3\)/.test(e))) {
    const keep = d.steps.filter((s) => s.strokes.length > 0);
    ART_PLAN_ERRORS.delete(spec.id);
    return withInfo({ ...d, steps: keep }, spec);
  }
  return withInfo(d, spec);
}

function withInfo(d: Drawing, spec: PictureSpec): Drawing {
  const picture: PictureInfo = {
    world: spec.world,
    of: spec.of,
    ...(spec.activity ? { activity: spec.activity } : {}),
    pose: spec.pose ?? [],
    place: spec.place,
    elements: spec.parts.map(({ part, label, kind }) => ({ part, label, kind })),
    light: pictureLight(spec),
  };
  return { ...d, activity: 'picture', picture };
}

/** Which steps draw / colour each element of a picture (step indexes). */
export function pictureTeaching(d: Drawing): Map<string, { draw: number[]; colour: number[] }> {
  const out = new Map<string, { draw: number[]; colour: number[] }>();
  for (const e of d.picture?.elements ?? []) out.set(e.part, { draw: [], colour: [] });
  d.steps.forEach((s, si) => {
    for (const st of s.strokes) {
      const m = /^[lf](\d+)$/.exec(st.ref ?? '');
      const l = m ? d.art?.layers[Number(m[1])] : undefined;
      const e = l?.p?.startsWith('bd:') ? out.get(l.p.slice(3)) : undefined;
      if (!e) continue;
      const list = st.tone === 'fill' ? e.colour : e.draw;
      if (!list.includes(si)) list.push(si);
    }
  });
  return out;
}

/** "Shown ⇒ taught" problems of a picture drawing (empty = OK). */
export function pictureProblems(d: Drawing): string[] {
  const out: string[] = [];
  const pic = d.picture;
  if (!pic || !d.art) return [`${d.id}: not a picture`];
  for (const e of ART_PLAN_ERRORS.get(d.id) ?? []) out.push(`${d.id} plan: ${e}`);
  const teach = pictureTeaching(d);
  for (const e of pic.elements) if (e.part === 'sign') out.push(`${d.id}: never name an element "sign" (it swallows the signature step); rename it, e.g. "signpost"`);
  for (const e of pic.elements) {
    const x = teach.get(e.part)!;
    if (!x.colour.length) out.push(`${d.id}: element "${e.part}" is never coloured`);
    if ((e.kind === 'scenery' || e.kind === 'prop') && !x.draw.length) out.push(`${d.id}: element "${e.part}" is never drawn`);
  }
  if (!pic.elements.some((e) => e.kind === 'sky')) out.push(`${d.id}: needs a sky (or wall / water) region`);
  if (!pic.elements.some((e) => e.kind === 'ground')) out.push(`${d.id}: needs a ground region`);
  const declared = new Set([...pic.elements.map((e) => `bd:${e.part}`), 'bd:light', 'bd:cast', 'bd:objCast', 'bd:sign']);
  for (const p of new Set(d.art.layers.map((l) => l.p ?? ''))) if (p.startsWith('bd:') && !declared.has(p)) out.push(`${d.id}: background part "${p.slice(3)}" is not declared as an element`);
  if (!d.steps.some((s) => s.strokes.some((x) => x.tone === 'fill' && /^l\d+$/.test(x.ref ?? '') && ['bd:cast', 'bd:objCast', '0:ground', 'bd:light'].includes(d.art!.layers[Number(x.ref!.slice(1))]?.p ?? '')))) out.push(`${d.id}: no light / shadow step`);
  return out;
}
